import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Box, Paper, Stack, TextField, Button, Typography, Alert } from '@mui/material';
import { api } from '../api/client';
import type { DriverDto } from '../api/types';
import { useAuthStore, hasAnyRole } from '../auth/store';

interface FormState {
  employeeCode: string;
  fullName: string;
  phone: string;
  email: string;
  notes: string;
}

const initialState: FormState = {
  employeeCode: '', fullName: '', phone: '', email: '', notes: '',
};

export function DriverFormPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const isNew = !id || id === 'new';
  const { user } = useAuthStore();
  const canEdit = hasAnyRole(user, 'ADMIN', 'OPERADOR');
  const [form, setForm] = useState<FormState>(initialState);
  const [err, setErr] = useState<string | null>(null);

  const existing = useQuery({
    queryKey: ['driver', id],
    queryFn: () => api.get<DriverDto>(`/drivers/${id}`).then((r) => r.data),
    enabled: !isNew,
  });

  useEffect(() => {
    if (existing.data) {
      setForm({
        employeeCode: existing.data.employeeCode ?? '',
        fullName: existing.data.fullName,
        phone: existing.data.phone ?? '',
        email: existing.data.email ?? '',
        notes: existing.data.notes ?? '',
      });
    }
  }, [existing.data]);

  const save = useMutation({
    mutationFn: () => {
      return isNew ? api.post('/drivers', form) : api.put(`/drivers/${id}`, form);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['drivers'] }); nav('/drivers'); },
    onError: (e: unknown) => {
      const ax = e as { response?: { data?: { message?: string } } };
      setErr(ax.response?.data?.message ?? 'Error al guardar');
    },
  });

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>{isNew ? 'Nuevo chofer' : 'Editar chofer'}</Typography>
      <Paper sx={{ p: 3, maxWidth: 720 }}>
        <Stack spacing={2}>
          {err && <Alert severity="error" onClose={() => setErr(null)}>{err}</Alert>}
          <TextField label="Código de empleado" value={form.employeeCode} onChange={(e) => setForm({ ...form, employeeCode: e.target.value })} disabled={!canEdit} />
          <TextField label="Nombre completo" value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} disabled={!canEdit} required />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label="Teléfono" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} disabled={!canEdit} fullWidth />
            <TextField label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!canEdit} fullWidth />
          </Stack>
          <TextField label="Notas" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} disabled={!canEdit} multiline rows={3} />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={() => nav('/drivers')}>Cancelar</Button>
            {canEdit && <Button variant="contained" onClick={() => save.mutate()} disabled={save.isPending}>Guardar</Button>}
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
